/* eslint-disable react/prefer-stateless-function */
import React, { useContext, useEffect } from 'react'
import { Box } from 'kilvin'
import { createComponent, RendererContext } from 'react-fela'
import Icon from 'design-framework/common/ui/Icon'
import CloseIcon from 'design-framework/common/icons/close-cross.svg'
import Button from 'design-framework/atoms/Button'
import Typography from 'design-framework/atoms/Typography'

import {
	__overlay,
	__header,
	__iconWrapper,
	__content,
	__subtitle,
	__footer,
	__container,
	__moveUp,
	__moveDown,
	__fadeIn,
	__fadeOut,
	__statusIcon,
} from './__style'
const ESC_KEY = 27
const Overlay = createComponent(__overlay)
const Container = createComponent(__container)
const Content = createComponent(__content, 'div', ['dangerouslySetInnerHTML'])
const Subtitle = createComponent(__subtitle, 'div', ['dangerouslySetInnerHTML'])
const Footer = createComponent(__footer)
const Header = createComponent(__header)
const IconWrapper = createComponent(__iconWrapper, 'div', [
	'onClick',
	'data-auto',
])
const StatusIcon = createComponent(__statusIcon)

const Buttons = ({ buttons = [], dispatch }) =>
	buttons.map(btn => (
		<Button
			key={btn.text}
			onClick={() => btn.action && btn.action(dispatch)}
			type={btn.inverted ? 'SECONDARY' : 'PRIMARY'}
			size='MEDIUM'
			data-auto={`cancel-${btn.text.toLowerCase()}`}
		>
			{btn.text}
		</Button>
	))

// @flow
type Props = {
	show?: boolean,
	config: {
		title: string,
		subtitle: string,
		content: string,
		closeAction: Function,
		verticalPosition: 'top' | 'middle' | 'bottom',
		margin: number,
		borderRadius: number,
		contentstart?: boolean,
		hideFooter?: boolean,
		nonCloseable?: boolean,
		buttons: Array<{
			text: string,
			action: Function,
			textColor: string,
			backgroundColor: string,
			inverted: boolean,
		}>,
	},
	hideFooter: boolean,
}

/** Change show prop to see the popup */
const Popup = ({ config, show, dispatch, overlayColor }: Props) => {
	const renderer = useContext(RendererContext)

	if (!config) return null
	// pulling down the store from context to give access to dispatcher.
	const {
		title,
		subtitle,
		content,
		verticalPosition,
		margin,
		buttons,
		borderRadius,
		contentstart,
		nonCloseable,
		hideFooter,
		closeAction,
		componentAsContent,
		statuIcon,
	} = config

	const moveUp = renderer.renderRule(__moveUp, { verticalPosition })
	const fadeIn = renderer.renderRule(__fadeIn)
	const fadeOut = renderer.renderRule(__fadeOut)
	const moveDown = renderer.renderRule(__moveDown, {
		verticalPosition,
	})

	useEffect(() => {
		const close = e => {
			if (e.keyCode === ESC_KEY && !nonCloseable && closeAction) {
				closeAction(dispatch)
			}
		}
		window.addEventListener('keydown', close)

		return () => window.removeEventListener('keydown', close)
	}, [])

	return (
		<Box>
			<Overlay
				className={show ? fadeIn : fadeOut}
				overlayColor={overlayColor}
			/>
			<Container
				className={show ? moveUp : moveDown}
				verticalPosition={verticalPosition}
				popupMargin={margin}
				popupRadius={borderRadius}
			>
				{/* If provided, to show status icon at the top of popUp */}
				{statuIcon && (
					<StatusIcon>
						<Icon glyph={statuIcon} />
					</StatusIcon>
				)}
				<Header>
					<Typography variant='subtitle1' color='darkGrey4' tag='div'>
						{title}
					</Typography>
					{!nonCloseable && (
						<IconWrapper
							onClick={() => closeAction && closeAction(dispatch)}
							data-auto='cancel-close'
						>
							<Icon glyph={CloseIcon} />
						</IconWrapper>
					)}
				</Header>
				{subtitle && (
					<Subtitle
						contentstart={contentstart}
						dangerouslySetInnerHTML={{
							__html: subtitle,
						}}
					/>
				)}
				{typeof content === 'string' ? (
					<Content
						dangerouslySetInnerHTML={{
							__html: content,
						}}
					/>
				) : (
					<Content contentstart={contentstart}>{content}</Content>
				)}
				{componentAsContent && componentAsContent}
				{!hideFooter && (
					<Footer>
						<Buttons dispatch={dispatch} buttons={buttons} />
					</Footer>
				)}
			</Container>
		</Box>
	)
}

Popup.defaultProps = {
	show: false,
	// eslint-disable-next-line react/default-props-match-prop-types
	hideFooter: false,
}

export default Popup
